import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Send, CheckCircle, ArrowRight, ArrowLeft, Upload, X, Music, Lock } from 'lucide-react';
import { useStore } from '../../data/store';

function isDeadlinePassed(deadline) {
  if (!deadline) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(deadline + 'T00:00:00') < today;
}

const genreOptions = ['Rock', 'Metal', 'Punk', 'Hardcore', 'Alternative', 'Sonstiges'];
const MAX_FILES = 3;
const MAX_SIZE_MB = 8;

export default function BandApplication() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [demoFiles, setDemoFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [ref, isVisible] = useScrollAnimation();
  const fileInputRef = useRef(null);
  const event = useStore((s) => s.event);
  const closed = isDeadlinePassed(event?.applicationsDeadline);
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm();
  const selectedGenre = watch('genre');
  const whatsappVal = watch('whatsapp');

  const handleFileChange = (e) => {
    setFileError('');
    const selected = Array.from(e.target.files || []);
    const allowed = selected.filter((f) => f.name.match(/\.(mp3|wav|ogg)$/i));
    const tooBig = allowed.filter((f) => f.size > MAX_SIZE_MB * 1024 * 1024);

    if (tooBig.length > 0) {
      setFileError(`Maximale Dateigröße: ${MAX_SIZE_MB} MB pro Datei`);
      return;
    }
    if (allowed.length < selected.length) {
      setFileError('Nur MP3, WAV und OGG Dateien erlaubt');
    }

    const combined = [...demoFiles, ...allowed].slice(0, MAX_FILES);
    setDemoFiles(combined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setDemoFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError('');
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('bandname', data.bandname);
      fd.append('genre', data.genre || '');
      fd.append('herkunft', data.herkunft || '');
      fd.append('members', data.members || '');
      fd.append('livePlayed', data.livePlayed || '');
      fd.append('musicUrl', data.musicUrl || '');
      fd.append('description', data.description || '');
      fd.append('email', data.email);
      fd.append('whatsapp', data.whatsapp || '');
      fd.append('whatsappConsent', data.whatsappConsent ? 'true' : 'false');
      demoFiles.forEach((f) => fd.append('demoFiles', f));

      const res = await fetch('/api/public/applications', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Fehler beim Senden');
      }

      setSubmitted(true);
      reset();
      setDemoFiles([]);
      setStep(1);
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      alert(err.message || 'Fehler beim Senden');
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep2 = () => {
    if (!watch('bandname') || !watch('genre')) return;
    setStep(2);
  };

  const goToStep3 = () => setStep(3);

  return (
    <section id="bewerben" className="bg-bg-primary section-padding min-h-[400px]">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center">
          <p className={`font-heading text-sm tracking-[4px] mb-4 ${closed ? 'text-text-muted' : 'text-accent-red'}`}>
            FÜR BANDS & KÜNSTLER
          </p>
          <h2 className="font-heading text-text-primary mb-6 md:mb-8" style={{ fontSize: 'clamp(28px, 7vw, 48px)' }}>
            {closed ? 'BEWERBUNGSPHASE BEENDET' : 'SPIELEN BEI ROCKNRHEIN'}
          </h2>
          <p className="font-body text-text-muted leading-relaxed text-base md:text-lg max-w-xl mx-auto mb-12 md:mb-16">
            {closed
              ? (event?.applicationClosedText || 'Die Bewerbungsphase ist abgeschlossen. Vielen Dank an alle Bands, die sich beworben haben!')
              : (event?.applicationOpenText || 'Rock\'n\'Rhein lebt von aufstrebenden Bands, die Bock haben alles zu geben. Wenn ihr eure Musik einem enthusiastischen Publikum zeigen wollt, bewirbt euch jetzt.')}
          </p>
        </div>

        {closed ? (
          <div className="bg-bg-card border border-divider p-10 text-center">
            <div className="w-16 h-16 bg-white/5 flex items-center justify-center mx-auto mb-6">
              <Lock size={36} className="text-text-muted" />
            </div>
            <h3 className="font-heading text-xl text-text-primary mb-3">BEWERBUNG GESCHLOSSEN</h3>
            <p className="font-body text-text-muted max-w-md mx-auto">
              {event?.applicationClosedText || 'Wir haben alle Bewerbungen erhalten und werden uns bei den ausgewählten Bands melden. Schaut auf unseren Social-Media-Kanälen vorbei für Updates zum Lineup!'}
            </p>
          </div>
        ) : submitted ? (
          <div className="bg-bg-card border border-green-500/30 p-8 text-center">
            <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
            <h3 className="font-heading text-2xl text-text-primary mb-2">BEWERBUNG EINGEGANGEN!</h3>
            <p className="font-body text-text-muted">{event?.applicationSuccessText || 'Vielen Dank! Wir melden uns bei euch.'}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-bg-card p-5 sm:p-8 md:p-10 border border-divider mt-12 md:mt-16">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-heading text-sm text-text-muted">Schritt {step} von 3</span>
              </div>
              <div className="w-full h-1.5 bg-bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent-red rounded-full transition-all duration-300"
                  style={{ width: step === 1 ? '33%' : step === 2 ? '66%' : '100%' }}
                />
              </div>
            </div>

            {step === 1 && (
              <div className="space-y-6">
                <Field label="Bandname *" error={errors.bandname}>
                  <input {...register('bandname', { required: 'Pflichtfeld' })} className="form-input" placeholder="Euer Bandname" />
                </Field>

                <div>
                  <label className="block font-heading text-sm tracking-wider text-text-primary mb-3">Genre *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {genreOptions.map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setValue('genre', g)}
                        className={`font-heading text-sm tracking-wider py-3 px-4 border-2 transition-all min-h-[44px] ${
                          selectedGenre === g
                            ? 'bg-accent-red border-accent-red text-white'
                            : 'border-divider text-text-muted hover:border-accent-red'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" {...register('genre', { required: 'Pflichtfeld' })} />
                  {errors.genre && <p className="text-accent-red text-xs mt-1 font-body">{errors.genre.message}</p>}
                </div>

                <Field label="Herkunftsort">
                  <input {...register('herkunft')} className="form-input" placeholder="z.B. Köln" />
                </Field>

                <button
                  type="button"
                  onClick={goToStep2}
                  className="w-full flex items-center justify-center gap-2 bg-accent-red text-white font-heading text-base tracking-wider px-8 border-2 border-accent-red hover:bg-transparent hover:text-accent-red transition-all"
                  style={{ height: '52px' }}
                >
                  WEITER
                  <ArrowRight size={18} />
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <Field label="Link zu eurer Musik">
                  <input {...register('musicUrl')} className="form-input" placeholder="Spotify, Bandcamp, YouTube..." />
                </Field>

                <Field label="Warum wollt ihr spielen?">
                  <textarea {...register('description')} className="form-input" style={{ minHeight: '100px', resize: 'vertical', paddingTop: '12px', paddingBottom: '12px' }} placeholder="Kurze Beschreibung..." rows={3} />
                </Field>

                <Field label="Kontakt E-Mail *" error={errors.email}>
                  <input {...register('email', { required: 'Pflichtfeld', pattern: { value: /^\S+@\S+$/i, message: 'Ungültige E-Mail' } })} type="email" className="form-input" placeholder="band@example.com" />
                </Field>

                <div>
                  <label className="block font-heading text-sm tracking-wider text-text-primary mb-2">WhatsApp-Nummer</label>
                  <input
                    {...register('whatsapp')}
                    type="tel"
                    className="form-input"
                    placeholder="+49 170 1234567"
                  />
                  <p className="font-body text-text-muted text-xs mt-1.5">Optional — damit wir euch direkt per WhatsApp informieren können.</p>
                </div>

                {whatsappVal && (
                  <div className="flex items-start gap-3 p-4 border border-divider bg-bg-secondary">
                    <input
                      type="checkbox"
                      id="whatsappConsent"
                      {...register('whatsappConsent')}
                      className="mt-0.5 shrink-0 accent-accent-red w-4 h-4"
                    />
                    <label htmlFor="whatsappConsent" className="font-body text-text-muted text-sm leading-relaxed cursor-pointer">
                      Ich bin damit einverstanden, dass Rock'n'Rhein mich über den Status meiner Bewerbung per WhatsApp kontaktiert.
                    </label>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 border-2 border-divider text-text-muted font-heading text-base tracking-wider px-8 hover:border-text-primary hover:text-text-primary transition-all"
                    style={{ height: '52px' }}
                  >
                    <ArrowLeft size={18} />
                    ZURÜCK
                  </button>
                  <button
                    type="button"
                    onClick={goToStep3}
                    className="w-full flex-1 flex items-center justify-center gap-2 bg-accent-red text-white font-heading text-base tracking-wider px-8 border-2 border-accent-red hover:bg-transparent hover:text-accent-red transition-all"
                    style={{ height: '52px' }}
                  >
                    WEITER
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block font-heading text-sm tracking-wider text-text-primary mb-2">Demo-Songs hochladen</label>
                  <p className="font-body text-text-muted text-sm mb-4">
                    Ladet bis zu {MAX_FILES} Songs hoch, die ihr beim Festival spielen würdet. Erlaubt: MP3, WAV, OGG — max. {MAX_SIZE_MB} MB pro Datei.
                  </p>

                  {demoFiles.length < MAX_FILES && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-divider text-text-muted font-body py-8 flex flex-col items-center gap-3 hover:border-accent-red hover:text-accent-red transition-all cursor-pointer"
                    >
                      <Upload size={28} />
                      <span className="text-sm">Datei auswählen ({demoFiles.length}/{MAX_FILES})</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.wav,.ogg,audio/mpeg,audio/wav,audio/ogg"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />

                  {fileError && <p className="text-accent-red text-xs mt-2 font-body">{fileError}</p>}

                  {demoFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {demoFiles.map((file, i) => (
                        <div key={i} className="flex items-center gap-3 bg-bg-secondary border border-divider px-4 py-3">
                          <Music size={16} className="text-accent-red shrink-0" />
                          <span className="font-body text-text-primary text-sm flex-1 truncate">{file.name}</span>
                          <span className="font-body text-text-muted text-xs shrink-0">{formatSize(file.size)}</span>
                          <button type="button" onClick={() => removeFile(i)} className="text-text-muted hover:text-accent-red transition-colors shrink-0">
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {demoFiles.length === 0 && (
                    <p className="font-body text-text-muted text-xs mt-3 text-center">
                      Optional — ihr könnt die Bewerbung auch ohne Demo-Songs absenden.
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 border-2 border-divider text-text-muted font-heading text-base tracking-wider px-8 hover:border-text-primary hover:text-text-primary transition-all"
                    style={{ height: '52px' }}
                  >
                    <ArrowLeft size={18} />
                    ZURÜCK
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex-1 flex items-center justify-center gap-2 bg-accent-red text-white font-heading text-base tracking-wider px-8 border-2 border-accent-red hover:bg-transparent hover:text-accent-red transition-all disabled:opacity-50"
                    style={{ height: '52px' }}
                  >
                    <Send size={18} />
                    {submitting ? 'SENDEN...' : 'BEWERBUNG ABSENDEN'}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #1c1c1c;
          border: 1px solid #333;
          padding: 12px 16px;
          color: #f5f5f5;
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          min-height: 48px;
        }
        .form-input:focus {
          outline: none;
          border-color: #1a5fb4;
        }
        .form-input::placeholder {
          color: #9e9e9e;
        }
      `}</style>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block font-heading text-sm tracking-wider text-text-primary mb-2">{label}</label>
      {children}
      {error && <p className="text-accent-red text-xs mt-1 font-body">{error.message}</p>}
    </div>
  );
}
